import type { IdentityLookupRow } from '../api/identityVerificationDashboardApi';
import {
  classifyMitekAuthenticity,
  mitekAuthenticityOperatorCopy,
} from './mitekAuthenticitySignal';
import type { IdentitySummaryTone } from './identitySessionSummary';

export type IdentityEvidenceOutcome = {
  /** Section topic — not a question (e.g. "Customer match"). */
  title: string;
  /** Short outcome chip. */
  answer: string;
  tone: IdentitySummaryTone;
  /** One plain sentence — what this means for the admin. */
  body: string;
};

function customerDisplayName(customer: {
  first_name: string | null;
  last_name: string | null;
  ccid: string;
} | null | undefined): string | null {
  if (!customer) return null;
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return name || customer.ccid;
}

/**
 * Customer-match outcome (implicitly: are they new to our system?).
 * Deduplication / returning selfie detail lives under the section.
 */
export function resolveCustomerMatchOutcome(row: IdentityLookupRow): IdentityEvidenceOutcome {
  const title = 'Customer match';
  const status = String(row.session_status ?? '').toUpperCase();
  const dedup = row.latestDedup;
  const bio = row.biometricMatchComparison ?? null;
  const hitName =
    customerDisplayName(dedup?.matched_hit_customer) ||
    (typeof dedup?.candidate_match?.ccid === 'string'
      ? String(dedup.candidate_match.ccid)
      : null);

  if (bio?.kind === 'RETURNING_SELFIE_1TO1') {
    if (bio.passed === true || status === 'SUCCESS' || status === 'COMPLETED') {
      return {
        title,
        answer: 'Already on file',
        tone: 'info',
        body: 'Returning customer — selfie matched the profile already in our system.',
      };
    }
    if (bio.passed === false) {
      return {
        title,
        answer: 'Selfie mismatch',
        tone: 'error',
        body: 'Returning login expected, but the selfie did not match the customer on file.',
      };
    }
  }

  if (dedup?.isDuplicate === true || status === 'REJECTED_DUPLICATE') {
    const who = hitName ? ` Looks like ${hitName}.` : '';
    if (status === 'REJECTED_DUPLICATE' || status === 'REJECTED' || status === 'FAILED') {
      return {
        title,
        answer: 'Already exists',
        tone: 'error',
        // Tier list lives in “What matched” chips — do not repeat dedup.message here.
        body: `Matches a customer already in our system and was blocked.${who}`,
      };
    }
    return {
      title,
      answer: 'Possible match',
      tone: 'warning',
      // Profile + What matched chips carry the detail; skip “Duplicate detected on: …”.
      body: '',
    };
  }

  if (dedup?.isDuplicate === false) {
    return {
      title,
      answer: 'No match found',
      tone: 'success',
      body: 'No matching customer already on file — looks new to our system.',
    };
  }

  if (bio?.kind === 'DEDUP_BIOMETRIC_HIT') {
    return {
      title,
      answer: 'Biometric hit',
      tone: 'warning',
      body: 'Biometric hit suggests this person may already be in our system. Review selfie match and duplicate-check details.',
    };
  }

  if (!dedup) {
    return {
      title,
      answer: 'Not checked yet',
      tone: 'info',
      body: 'No duplicate-check session is on file for this attempt yet.',
    };
  }

  return {
    title,
    answer: 'Check incomplete',
    tone: 'info',
    body: 'A duplicate check ran, but there is no clear new-vs-existing outcome yet.',
  };
}

/**
 * After sign-in / returning flow — confirm same account via biometric when available.
 */
export function resolveReturningCustomerOutcome(row: IdentityLookupRow): IdentityEvidenceOutcome {
  const title = 'Returning customer check';
  const status = String(row.session_status ?? '').toUpperCase();
  const route = String(row.verification_route ?? '').toUpperCase();
  const bio = row.biometricMatchComparison ?? null;
  const onFile = customerDisplayName(row.customer) || row.ccid || null;
  const completedOk =
    status === 'SUCCESS' || status === 'COMPLETED' || status === 'IDV_COMPLETED';

  if (bio?.kind === 'RETURNING_SELFIE_1TO1') {
    if (bio.passed === true || (bio.passed !== false && completedOk)) {
      return {
        title,
        answer: 'Selfie matched',
        tone: 'success',
        body: onFile
          ? `Login selfie matched the biometric on file for ${onFile}.`
          : 'Login selfie matched the biometric already on file.',
      };
    }
    if (bio.passed === false) {
      return {
        title,
        answer: 'Selfie mismatch',
        tone: 'error',
        body: 'Login selfie did not match the customer on file after sign-in.',
      };
    }
  }

  if (bio?.kind === 'DEDUP_BIOMETRIC_HIT') {
    if (bio.passed === true) {
      return {
        title,
        answer: 'Biometric matched',
        tone: 'success',
        body: onFile
          ? `Biometric comparison confirmed the same person as ${onFile}.`
          : 'Biometric comparison confirmed the same person already on file.',
      };
    }
    if (bio.passed === false) {
      return {
        title,
        answer: 'Biometric mismatch',
        tone: 'error',
        body: 'Biometric comparison did not confirm the signed-in customer.',
      };
    }
  }

  // Selfie-only returning route completed — biometric ran even if comparison payload is thin.
  if (route === 'RETURNING_SELFIE_1TO1' && completedOk) {
    return {
      title,
      answer: 'Selfie matched',
      tone: 'success',
      body: onFile
        ? `Returning biometric selfie check confirmed this is still ${onFile}.`
        : 'Returning biometric selfie check confirmed the same customer account.',
    };
  }

  if (completedOk || (row.ccid && row.latestDedup?.isDuplicate === false)) {
    return {
      title,
      answer: 'Same account confirmed',
      tone: 'success',
      body: onFile
        ? `After sign-in, identity checks confirmed this is still ${onFile}.`
        : 'After sign-in, identity checks confirmed the same customer account.',
    };
  }

  if (status === 'FAILED' || status === 'REJECTED' || status === 'FAILED_RETURNING_SELFIE') {
    return {
      title,
      answer: 'Did not confirm',
      tone: 'error',
      body: 'Returning-customer checks did not confirm the signed-in account.',
    };
  }

  return {
    title,
    answer: 'In progress',
    tone: 'info',
    body: 'Returning-customer comparison has started but is not finished yet.',
  };
}

/**
 * Document authenticity — only meaningful when the document vendor actually ran.
 * Callers should skip rendering when this returns null.
 */
export function resolveDocumentAuthenticityOutcome(
  row: IdentityLookupRow,
): IdentityEvidenceOutcome | null {
  if (!row.mitekCalled) return null;

  const title = 'Document authenticity';
  const signal = classifyMitekAuthenticity(row);

  if (signal === 'hard_fail') {
    const copy = mitekAuthenticityOperatorCopy(row, 'hard_fail');
    return {
      title,
      answer: 'Failed',
      tone: 'error',
      body: copy.body,
    };
  }

  if (signal === 'review') {
    const copy = mitekAuthenticityOperatorCopy(row, 'review');
    return {
      title,
      answer: 'Needs review',
      tone: 'warning',
      body: copy.body,
    };
  }

  return {
    title,
    answer: 'Passed',
    tone: 'success',
    body: 'Mitek verification passed. The ID looks authentic for who they claim to be.',
  };
}
